'use client';

import { useListItem, useMergeRefs } from '@floating-ui/react';
import { type MouseEventHandler, forwardRef, useCallback, useContext, useRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledAnchor } from '../../hooks/use-disabled-anchor';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { Slot } from '../slot';
import { MenuItemContext } from './context/menu-item.context';
import { MenuRootContext } from './context/menu-root.context';
import { menuItemRecipe } from './styles/menu-item.styles';
import type { MenuItemPropsInterface } from './types/menu-item.types';

/**
 * @since 10.0.0
 */
export const MenuItem = forwardRef<HTMLButtonElement, MenuItemPropsInterface>(
  (props: MenuItemPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      css: cssProp,
      className,
      disabled = false,
      onClick,
      ...rest
    } = props;

    const { menu } = useContext(MenuRootContext);
    const localRef = useRef<HTMLButtonElement>();
    const { ref: itemRef, index } = useListItem();
    const mergedRefs = useMergeRefs([itemRef, localRef, forwardedRef]);
    const isActive = menu.activeIndex === index;

    const styles = css(menuItemRecipe.raw({ disabled }), cssProp);

    const Component = asChild ? Slot : 'button';

    const a11yDisabledState = useDisabledState({ disabled });

    const content = useDisabledAnchor({ disabled, asChild, children });

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        if (disabled) {
          return;
        }
        // Do not close menu if the Menu.Item itself is a trigger for another floating element
        const { current } = localRef;
        if (
          !current?.hasAttribute('aria-controls') &&
          !current?.hasAttribute('aria-expanded') &&
          !current?.hasAttribute('aria-haspopup')
        ) {
          menu.onClose?.(event);
        }

        onClick?.(event);
      },
      [disabled, menu, onClick],
    );

    return (
      <MenuItemContext.Provider value={{ anchor: asChild, disabled }}>
        <Component
          ref={mergedRefs}
          role="menuitem"
          tabIndex={isActive && !disabled ? 0 : -1}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Menu.Item"
          className={cx('c11n', 'group', styles, className)}
          type={asChild ? undefined : 'button'}
          {...menu.getItemProps({
            onClick: onClickHandler,
            ...rest,
          })}
          {...a11yDisabledState}
        >
          {content}
        </Component>
      </MenuItemContext.Provider>
    );
  },
);

MenuItem.displayName = 'Menu.Item';
